import { useQuery } from '@apollo/client'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { UserProvider } from '@faceup/member'
import { Chat, ChatInput, useUnreadFollowUpMessages } from '@faceup/report'
import { Card } from '@faceup/ui'
import { Alert, Input, Typography } from '@faceup/ui-base'
import { ChannelType, compact, NUMBER_FOR_NO_LIMIT } from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { type ComponentProps, useMemo, useState } from 'react'
import { graphql } from '../__generated__'
import useAnalytics from '../utils/analytics'

const ReportChatQuery = graphql(`
  query ReportChatQuery($followUpFirst: Int!) {
    companyVictimViewer {
      id
      case {
        id
        status {
          id
          groupName
        }
        channel {
          id
          sourceType
          defaultLanguage
        }
        followUpActivities(first: $followUpFirst) {
          __typename
          items {
            id
            ...useUnreadFollowUpMessages_followupComment
          }
        }
      }
    }
  }
`)

const messages = defineMessages('followUp.components.reportChat', [
  { id: 'title', defaultMessage: '' },
  { id: 'messagingInfo', defaultMessage: '' },
  { id: 'writeNote', defaultMessage: '' },
  { id: 'empty', defaultMessage: '' },
  { id: 'noteLabel', defaultMessage: '' },
  { id: 'administrator', defaultMessage: '' },
  { id: 'leaveQuestion', defaultMessage: '' },
  { id: 'reopen', defaultMessage: '' },
  { id: 'addAttachments', defaultMessage: '' },
  { id: 'attachmentsTitle', defaultMessage: '' },
  { id: 'detectLanguage', defaultMessage: '' },
  { id: 'translate', defaultMessage: '' },
  { id: 'showOriginal', defaultMessage: '' },
  { id: 'to', defaultMessage: '' },
  { id: 'sameLanguageError', defaultMessage: '' },
  { id: 'disabledReasonE2EE', defaultMessage: '' },
  { id: 'onUseWarningTitle', defaultMessage: '' },
  { id: 'onUseWarningContent', defaultMessage: '' },
  { id: 'onUseWarningDoNotShowAgain', defaultMessage: '' },
] as const)

const closedMessagesPerChannel = defineMessages(
  'followUp.components.reportChat.closedMessagesPerChannel',
  Object.values(ChannelType).map(id => ({ id, defaultMessage: '' }))
)

const archivedMessagesPerChannel = defineMessages(
  'followUp.components.reportChat.archivedMessagesPerChannel',
  Object.values(ChannelType).map(id => ({ id, defaultMessage: '' }))
)

type ReportChatQuery = ResultOf<typeof ReportChatQuery>

export const ReportChatPreview = () => {
  const { formatMessage } = useIntl()
  return (
    <Card className='p-8 w-full flex flex-col gap-4'>
      <Typography.Title level={4} className='m-0!'>
        <FormattedMessage message={messages.title} />
      </Typography.Title>
      <Alert type='warning' message={<FormattedMessage message={messages.messagingInfo} />} />
      <Input.TextArea rows={3} placeholder={formatMessage(messages.writeNote)} disabled />
    </Card>
  )
}

type ReportChatProps = {
  isLoggedIn: boolean
}

export const ReportChat = ({ isLoggedIn }: ReportChatProps) => {
  const { formatMessage } = useIntl()
  const { track } = useAnalytics()
  const [alertDismissed, setAlertDismissed] = useState(false)

  const { data, loading } = useQuery(ReportChatQuery, {
    fetchPolicy: 'network-only',
    skip: !isLoggedIn,
    variables: { followUpFirst: NUMBER_FOR_NO_LIMIT },
  })

  if (!isLoggedIn || loading) {
    return <Card loading className='w-full p-8' />
  }

  const viewer = data?.companyVictimViewer
  const aCase = viewer?.case

  if (!viewer || !aCase) {
    return <Card loading className='w-full p-8' />
  }

  const hasMessages = (aCase.followUpActivities?.items?.length ?? 0) > 0

  const showAlert = !hasMessages && !alertDismissed

  return (
    <UserProvider
      member={null}
      application='follow-up'
      userId={viewer.id}
      onChangeLanguage={() => null}
      onLogout={() => null}
    >
      <Card className='p-8 w-full flex flex-col gap-4' data-test='report-chat'>
        <Typography.Title level={4} className='m-0!'>
          <FormattedMessage message={messages.title} />
        </Typography.Title>
        {showAlert && (
          <Alert
            type='warning'
            closable
            onClose={() => setAlertDismissed(true)}
            message={<FormattedMessage message={messages.messagingInfo} />}
          />
        )}
        <ChatInner
          viewer={viewer}
          onMessageSend={() => {
            track('follow_up_message_sent')
            setAlertDismissed(true)
          }}
          caseId={aCase.id}
          userId={viewer.id}
          relativeMessages={{
            type: 'Victim',
            ...messages,
            closedMessagesPerChannel,
            archivedMessagesPerChannel,
          }}
          input={props => (
            <ChatInput
              {...props}
              inputRef={null}
              mentions={null}
              placeholder={formatMessage(messages.writeNote)}
            />
          )}
        />
      </Card>
    </UserProvider>
  )
}

type ChatInnerProps = {
  viewer: NonNullable<ReportChatQuery['companyVictimViewer']>
} & Omit<ComponentProps<typeof Chat>, 'firstUnreadMessageId'>

const ChatInner = ({ viewer, ...props }: ChatInnerProps) => {
  const comments = useMemo(() => compact(viewer.case?.followUpActivities?.items ?? []), [viewer])
  const { firstUnreadMessageId } = useUnreadFollowUpMessages(comments)

  return <Chat {...props} firstUnreadMessageId={firstUnreadMessageId ?? ''} />
}
